import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0, type User as Auth0User } from '@auth0/auth0-react';

export const authUserKey = 'user';

type AuthUser = {
  id: string;
  email: string;
  emailVerified: boolean;
  picture?: string;
};

export const auth0Scope = 'openid profile email offline_access';

const accessTokenRequestOptions = {
  timeoutInSeconds: 5,
  authorizationParams: {
    scope: auth0Scope,
  },
};

export const saveAuthUser = (auth0User: Auth0User) => {
  const authUser = parseAuth0User(auth0User);
  localStorage.setItem(authUserKey, JSON.stringify(authUser));
};

export const parseAuth0User = (auth0User: Auth0User): AuthUser => {
  return {
    id: auth0User['sub']!.split('|')[1],
    email: auth0User.email!,
    emailVerified: auth0User.email_verified!,
    picture: auth0User.picture!,
  };
};

export const getAuthUser = (): AuthUser => {
  const authUserStr = localStorage.getItem(authUserKey);
  // The stored user is absent only pre-login/post-logout; ProtectedRoutes validates `id`
  // at runtime before any authed route renders, so the required type holds inside the app.
  const authUser: AuthUser = authUserStr ? JSON.parse(authUserStr) : ({} as AuthUser);
  return authUser;
};

export const useAccessToken = (logoutRoute = '/logout') => {
  const [accessToken, setAccessToken] = useState<string>();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    getAccessTokenSilently(accessTokenRequestOptions)
      .then((token) => {
        setAccessToken(token);
      })
      .catch((e) => {
        logAccessTokenFailure(e);
        navigate(logoutRoute);
      });
  }, [isAuthenticated, getAccessTokenSilently, logoutRoute, navigate]);

  return accessToken;
};

// Returns a stable getter so request-time callers (e.g. the agent session) can resolve a fresh
// token instead of relying on the value captured once by useAccessToken. Failures resolve to null
// rather than navigating to a logout route — callers fall back to whatever token they already have.
export const useAccessTokenGetter = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  return useCallback(async (): Promise<string | null> => {
    if (!isAuthenticated) return null;

    try {
      return await getAccessTokenSilently(accessTokenRequestOptions);
    } catch (e) {
      logAccessTokenFailure(e);
      return null;
    }
  }, [isAuthenticated, getAccessTokenSilently]);
};

// Auth0 rejects with these codes whenever the refresh token is simply gone — cross-tab logout,
// rotation, an expired session. They are expected, so they log at warn: `captureConsoleIntegration`
// only turns console.error into Sentry events, and real token failures (`timeout`, network,
// misconfiguration) must keep reaching it. See DM-1479.
const EXPECTED_AUTH_TOKEN_ERRORS = new Set(['invalid_grant', 'missing_refresh_token', 'login_required']);

const logAccessTokenFailure = (error: unknown): void => {
  const code = typeof error === 'object' && error !== null ? (error as { error?: unknown }).error : undefined;

  if (typeof code === 'string' && EXPECTED_AUTH_TOKEN_ERRORS.has(code)) {
    console.warn('could not get access token', error);
    return;
  }

  console.error('could not get access token', error);
};
